import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, clsx, Icon, withClassName } from '@a-type/ui';
import { memo } from 'react';
import { PlayerAvatar } from '../players/PlayerAvatar.js';
import cls from './PlayingCard.module.css';
const suitToIcon = {
    h: 'suitHeart',
    d: 'suitDiamond',
    c: 'suitClub',
    s: 'suitSpade',
};
const PlayingCardRoot = memo(function PlayingCardRoot({ cardSuit, cardRank, disabled, playerId, className, size, style: userStyle, ...rest }) {
    const color = getCardColor(cardSuit);
    const sizeStyle = size ? { width: size, ...userStyle } : userStyle;
    return (_jsxs(CardRoot, { "data-color": color, "data-suit": cardSuit, "data-disabled": disabled, className: clsx('playing-card', `@mode-${color === 'red' ? 'tomato' : 'neutral'}`, className), style: sizeStyle, ...rest, children: [_jsx(ExtremelySimpleCardContent, { cardSuit: cardSuit, cardRank: cardRank }), _jsx(SimpleCardContent, { cardSuit: cardSuit, cardRank: cardRank }), _jsx(DetailedCardContent, { cardSuit: cardSuit, cardRank: cardRank }), disabled && _jsx(Slash, {}), playerId && _jsx(PlayerAvatar, { playerId: playerId, className: cls.avatar })] }));
});
function ExtremelySimpleCardContent({ cardSuit, cardRank, }) {
    return (_jsxs(Box, { col: true, layout: "center center", full: true, className: cls.extremelySimple, border: true, children: [_jsx(CardNumber, { cardRank: cardRank }), _jsx(CardSuitIcon, { cardSuit: cardSuit })] }));
}
function SimpleCardContent({ cardSuit, cardRank, }) {
    return (_jsxs(Box, { col: true, layout: "center center", full: true, p: "xs", className: cls.simple, border: true, children: [_jsxs(Box, { col: true, className: cls.indicatorTL, children: [_jsx(CardNumber, { cardRank: cardRank, className: cls.rankLeft }), _jsx(CardSuitIcon, { cardSuit: cardSuit })] }), _jsxs(Box, { col: true, className: cls.indicatorBR, children: [_jsx(CardNumber, { cardRank: cardRank, className: cls.rankRight }), _jsx(CardSuitIcon, { cardSuit: cardSuit })] })] }));
}
const symbolPatterns = [
    // 1
    [{ x: 0.5, y: 0.5 }],
    // 2
    [
        { x: 0.5, y: 0.25 },
        { x: 0.5, y: 0.75 },
    ],
    // 3
    [
        { x: 0.5, y: 0.5 },
        { x: 0.5, y: 0.25 },
        { x: 0.5, y: 0.75 },
    ],
    // 4
    [
        { x: 0.25, y: 0.25 },
        { x: 0.75, y: 0.25 },
        { x: 0.25, y: 0.75 },
        { x: 0.75, y: 0.75 },
    ],
    // 5
    [
        { x: 0.25, y: 0.25 },
        { x: 0.75, y: 0.25 },
        { x: 0.25, y: 0.75 },
        { x: 0.75, y: 0.75 },
        { x: 0.5, y: 0.5 },
    ],
    // 6
    [
        { x: 0.25, y: 0.25 },
        { x: 0.75, y: 0.25 },
        { x: 0.25, y: 0.5 },
        { x: 0.75, y: 0.5 },
        { x: 0.25, y: 0.75 },
        { x: 0.75, y: 0.75 },
    ],
    // 7
    [
        { x: 0.25, y: 0.25 },
        { x: 0.75, y: 0.25 },
        { x: 0.25, y: 0.5 },
        { x: 0.75, y: 0.5 },
        { x: 0.25, y: 0.75 },
        { x: 0.75, y: 0.75 },
        { x: 0.5, y: 0.35 },
    ],
    // 8
    [
        { x: 0.25, y: 0.25 },
        { x: 0.75, y: 0.25 },
        { x: 0.25, y: 0.5 },
        { x: 0.75, y: 0.5 },
        { x: 0.25, y: 0.75 },
        { x: 0.75, y: 0.75 },
        { x: 0.5, y: 0.35 },
        { x: 0.5, y: 0.65 },
    ],
    // 9
    [
        { x: 0.25, y: 0.25 },
        { x: 0.75, y: 0.25 },
        { x: 0.25, y: 0.4 },
        { x: 0.75, y: 0.4 },
        { x: 0.25, y: 0.6 },
        { x: 0.75, y: 0.6 },
        { x: 0.25, y: 0.75 },
        { x: 0.75, y: 0.75 },
        { x: 0.5, y: 0.5 },
    ],
    // 10
    [
        { x: 0.25, y: 0.2 },
        { x: 0.75, y: 0.2 },
        { x: 0.25, y: 0.4 },
        { x: 0.75, y: 0.4 },
        { x: 0.25, y: 0.6 },
        { x: 0.75, y: 0.6 },
        { x: 0.25, y: 0.8 },
        { x: 0.75, y: 0.8 },
        { x: 0.5, y: 0.35 },
        { x: 0.5, y: 0.65 },
    ],
];
function DetailedCardContent({ cardSuit, cardRank, }) {
    let symbolCount = cardRank;
    if (symbolCount > 10) {
        symbolCount = 1;
    }
    const pattern = symbolPatterns[symbolCount - 1];
    return (_jsxs(Box, { layout: "center center", full: true, col: true, border: true, className: cls.detailed, children: [_jsxs(NumberSuitStack, { className: cls.indicatorTL, children: [_jsx(CardNumber, { cardRank: cardRank }), _jsx(CardSuitIcon, { cardSuit: cardSuit })] }), _jsxs(NumberSuitStack, { className: cls.indicatorBR, children: [_jsx(CardNumber, { cardRank: cardRank }), _jsx(CardSuitIcon, { cardSuit: cardSuit })] }), _jsx(Box, { grow: true, className: cls.detailedCenter, children: pattern.map((pos, index) => (_jsx(CardSymbol, { cardSuit: cardSuit, cardRank: cardRank, className: cls.detailedSymbol, style: {
                        left: `${pos.x * 100}%`,
                        top: `${pos.y * 100}%`,
                        transform: 'translate(-50%, -50%)',
                        width: `${Math.max(15, 80 / symbolCount)}%`,
                        height: 'auto',
                    } }, index))) })] }));
}
const NumberSuitStack = withClassName('div', cls.suitStack);
export function PlayingCardPlaceholder({ children, className, size, style: userStyle, ...rest }) {
    const sizeStyle = size ? { width: size, ...userStyle } : userStyle;
    return (_jsx(CardRoot, { className: clsx(className, cls.placeholderRoot), style: sizeStyle, container: true, "data-disabled": true, ...rest, children: _jsx(Box, { border: true, full: true, className: cls.placeholder, children: children }) }));
}
export const PlayingCard = Object.assign(PlayingCardRoot, {
    Placeholder: PlayingCardPlaceholder,
});
const CardRoot = withClassName(Box, cls.cardRoot);
function ScalingText({ children, className, style, ...rest }) {
    return (_jsx("svg", { className: clsx(cls.text, className), viewBox: "0 0 100 100", xmlns: "http://www.w3.org/2000/svg", style: style, ...rest, children: _jsx("text", { x: "50%", y: "50%", dominantBaseline: "middle", textAnchor: "middle", fontSize: "80", fill: "currentColor", fontWeight: "600", children: children }) }));
}
function CardNumber({ cardRank, className, }) {
    return (_jsx(ScalingText, { className: clsx(cls.generalSymbol, className), children: toDisplayRank(cardRank) }));
}
function CardSuitIcon({ cardSuit, className, style, }) {
    return (_jsx(Icon, { name: suitToIcon[cardSuit], className: clsx(cls.generalSymbol, cls.suitIcon, className), vectorEffect: "non-scaling-stroke", style: style }));
}
function CardSymbol({ className, style, cardSuit, cardRank, }) {
    if (cardRank <= 10) {
        return (_jsx(CardSuitIcon, { cardSuit: cardSuit, className: className, style: style }));
    }
    return (_jsx(ScalingText, { className: clsx(cls.generalSymbol, className), style: style, children: toDisplayRank(cardRank) }));
}
function Slash() {
    return (_jsx("svg", { className: cls.slash, viewBox: "0 0 100 100", children: _jsx("line", { x1: "90", y1: "-10", x2: "10", y2: "110", strokeWidth: "1", vectorEffect: "non-scaling-stroke" }) }));
}
function getCardColor(suit) {
    return suit === 'h' || suit === 'd' ? 'red' : 'black';
}
function toDisplayRank(rank) {
    if (rank === 1 || rank === 14)
        return 'A';
    if (rank === 11)
        return 'J';
    if (rank === 12)
        return 'Q';
    if (rank === 13)
        return 'K';
    return rank.toString();
}
//# sourceMappingURL=PlayingCard.js.map